import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { getUserStoreProducts } from "../../../firebase/product-functions";
import "./ItemSection.css";
import ProductGrid from "../../common/ProductGrid/ProductGrid";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import { useLanguage } from "../../../context/LanguageContext";
import useCurrency from "../../../utils/currencyUtils";

const ItemSection = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currency: storeCurrency } = useStoreSettings();
  const { language } = useLanguage();
  const { formatDisplay } = useCurrency();

  // Translations for section title
  const translations = {
    en: {
      newArrivals: "NEW ARRIVALS",
      shopAll: "SHOP ALL",
      loading: "Loading new arrivals...",
      emptyState: "No new arrivals available",
      viewAllProducts: "View All Products",
    },
    ar: {
      newArrivals: "وصل حديثاً",
      shopAll: "تسوق الكل",
      loading: "جاري تحميل الوصولات الحديثة...",
      emptyState: "لا توجد وصولات حديثة متاحة",
      viewAllProducts: "عرض جميع المنتجات",
    },
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const storeId = DEFAULT_STORE_ID;
        const cacheKey = `homeProducts_${storeId}`;
        const cachedProducts = sessionStorage.getItem(cacheKey);
        const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);

        // Use cache if it's less than 3 minutes old
        if (cachedProducts && cacheTimestamp) {
          const age = Date.now() - parseInt(cacheTimestamp);
          if (age < 3 * 60 * 1000) {
            // 3 minutes cache
            const parsedProducts = JSON.parse(cachedProducts);
            setProducts(parsedProducts);
            setLoading(false);
            return;
          }
        }

        // Use the centralized function
        const result = await getUserStoreProducts(storeId, {
          limit: 8,
          status: "active",
          sortBy: "createdAt",
          sortDirection: "asc",
        });

        const productsData = result.products.map((product) => {
          const image =
            product.images && product.images.length > 0
              ? product.images[0]
              : "https://via.placeholder.com/300x300?text=No+Image";

          // Determine stock status
          let isOutOfStock = false;
          if (product.variants && product.variants.length > 0) {
            isOutOfStock = product.variants.every((v) => v.quantity <= 0);
          } else {
            isOutOfStock = product.quantity <= 0;
          }

          return {
            ...product,
            storeId: storeId,
            image,
            isOutOfStock,
          };
        });

        setProducts(productsData);

        // Cache the products
        sessionStorage.setItem(cacheKey, JSON.stringify(productsData));
        sessionStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const formatCurrency = formatDisplay;

  return (
    <div className="item-section-container">
      <div className="section-header">
        <h2 className="modern-title">{t.newArrivals}</h2>
        <button className="shop-all-btn" onClick={() => navigate("/shop")}>
          {t.shopAll}
        </button>
      </div>

      <ProductGrid
        products={products}
        loading={loading}
        error={error}
        formatCurrency={formatCurrency}
        gridColumns={4}
        cardVariant="items"
        loadingText={t.loading}
        emptyStateText={t.emptyState}
        emptyStateAction={{
          text: t.viewAllProducts,
          onClick: () => navigate("/shop"),
        }}
        className="item-section-grid"
      />
    </div>
  );
};

export default ItemSection;
